import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import FeaturedImage from '@/components/FeaturedImage';
import GalleryStrip from '@/components/GalleryStrip';
import { useSite } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';

const WORKSHOPS = [
    { title: 'Bouquet stagionali', desc: 'Composizioni che seguono il ritmo delle stagioni.' },
    { title: 'Flower box', desc: 'Le scatole-fiore: gesto elegante e duraturo.' },
    { title: 'Sushi flower', desc: 'Mini-composizioni d’autore, perfette come regalo.' },
    { title: 'Kokedama', desc: 'L’arte giapponese delle piante in sfera di muschio.' },
    { title: 'Colombe fiorite', desc: 'Decorazioni pasquali dolci e raffinate.' },
    { title: 'Zucche fiorite', desc: 'Composizioni autunnali su zucche naturali.' },
    { title: 'Ghirlande decorative', desc: 'Corone per la porta, per le stagioni e per le feste.' },
    { title: 'Centrotavola natalizi', desc: 'Atmosfere d’inverno per la tavola delle feste.' },
    { title: 'Addii al nubilato', desc: 'Esperienze floreali su misura per gruppi e momenti speciali tra amiche.' },
    { title: 'Eventi privati e team building', desc: 'Esperienze su misura per gruppi e aziende.' },
];

export default function Workshop() {
    const { t } = useSite();
    useSeo({
        title: `${SITE_NAME} — Workshop floreali`,
        description: "Workshop floreali a Lomazzo: bouquet, flower box, kokedama, ghirlande, centrotavola natalizi, addii al nubilato. Esperienze creative.",
    });
    return (
        <div data-testid={TEST_IDS.pageWorkshop}>
            {/* Hero */}
            <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20">
                <div className="container-atelier grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <p className="eyebrow mb-4">{t('workshop.hero.eyebrow', 'Workshop Floreal')}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t('workshop.hero.title', "Impara l'arte della composizione floreale.")}
                        </h1>
                        <p className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                            {t('workshop.hero.description', 'I nostri workshop sono momenti di condivisione, creatività e scoperta.')}
                        </p>
                        <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-prose">
                            {t('workshop.hero.description2', 'Guidati dal nostro team, imparerai tecniche professionali di composizione e decorazione botanica in un ambiente accogliente e stimolante.')}
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <FeaturedImage
                            slot="hero_workshop"
                            alt="Workshop floreale in atelier Floreal"
                            priority
                            className="rounded-xl aspect-[4/5] w-full shadow-soft"
                            fallbackLabel="Workshop"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Esperienze floreali */}
            <Section title={t('workshop.services.title', 'Esperienze floreali')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {WORKSHOPS.map((w, i) => (
                        <motion.article
                            key={w.title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.35, delay: (i % 3) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-xl border border-border bg-card p-6"
                        >
                            <div className="flex items-start gap-3">
                                <span className="h-7 w-7 rounded-full bg-atelier-sage/20 flex items-center justify-center shrink-0 mt-0.5">
                                    <Check className="h-3.5 w-3.5 text-atelier-sage-ink" strokeWidth={2.5} />
                                </span>
                                <div>
                                    <h3 className="font-serif text-xl tracking-tight">{w.title}</h3>
                                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </Section>

            {/* Photo strip — solo workshop */}
            <Section className="py-8 sm:py-12">
                <GalleryStrip
                    category="workshop"
                    limit={4}
                    tileClass="rounded-lg aspect-[4/5]"
                    ariaLabel="Workshop floreali di Floreal"
                />
            </Section>

            {/* CTA */}
            <Section className="py-12 sm:py-16">
                <div className="rounded-xl bg-atelier-ivory border border-atelier-sage/25 px-6 sm:px-10 py-12 sm:py-16 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl mx-auto leading-[1.1]">
                        {t('workshop.cta.title', 'Scopri i prossimi appuntamenti.')}
                    </h3>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="h-12 px-6">
                            <Link to="/eventi">{t('workshop.cta.btn_events', 'Vai agli eventi')}</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background">
                            <Link to="/contatti#form">{t('workshop.cta.btn_info', 'Richiedi informazioni')}</Link>
                        </Button>
                    </div>
                </div>
            </Section>
        </div>
    );
}
